import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { 
  PieChart, 
  BarChart3, 
  TrendingUp, 
  Download, 
  Calendar,
  IndianRupee,
  Users,
  Activity,
  FileText,
  Plus
} from 'lucide-react';
import { Button } from '../../components/ui/Button/Button';
import { Select } from '../../components/ui/Select';
import { ResponsiveTable } from '../../components/ui/table';
import { 
  generateReport, 
  saveReport,
  TIME_PERIOD,
  REPORT_TYPE 
} from '../../store/financeReportSlice';

export function FinancialReportsPage() {
  const dispatch = useDispatch();
  const financeReports = useSelector(state => state.financeReports);
  const { financialData, kpis, savedReports, isGenerating } = financeReports;
  
  const [selectedPeriod, setSelectedPeriod] = useState(TIME_PERIOD.MONTHLY);
  const [selectedReportType, setSelectedReportType] = useState(REPORT_TYPE.REVENUE);
  const [activeTab, setActiveTab] = useState('dashboard');

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatPercentage = (value) => {
    return `${value > 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

  const getChangeColor = (change) => {
    return change > 0 ? 'text-green-600' : change < 0 ? 'text-red-600' : 'text-gray-600';
  };

  const handleGenerateReport = () => {
    dispatch(generateReport({
      reportType: selectedReportType,
      period: selectedPeriod,
      dateRange: {
        start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
        end: new Date().toISOString()
      }
    }));
  };

  // Revenue Chart Configuration
  const revenueChartOptions = {
    chart: {
      type: 'line',
      backgroundColor: 'transparent',
      height: 400
    },
    title: {
      text: 'Revenue Trend',
      style: { fontSize: '18px', fontWeight: 'bold' }
    },
    xAxis: {
      categories: financialData.revenue[selectedPeriod]?.map(item => 
        selectedPeriod === TIME_PERIOD.MONTHLY ? `${item.month} ${item.year}` :
        selectedPeriod === TIME_PERIOD.WEEKLY ? item.week :
        item.date
      ) || []
    },
    yAxis: {
      title: { text: 'Revenue ($)' },
      labels: {
        formatter: function() {
          return '$' + (this.value / 1000) + 'K';
        }
      }
    },
    series: [{
      name: 'Revenue',
      data: financialData.revenue[selectedPeriod]?.map(item => item.value) || [],
      color: '#3B82F6',
      lineWidth: 3,
      marker: {
        radius: 5,
        fillColor: '#3B82F6'
      }
    }],
    tooltip: {
      formatter: function() {
        return `<b>${this.x}</b><br/>Revenue: ${formatCurrency(this.y)}`;
      }
    },
    credits: { enabled: false },
    legend: { enabled: false }
  };

  // Expenses Breakdown Chart
  const expensesChartOptions = {
    chart: {
      type: 'pie',
      backgroundColor: 'transparent',
      height: 400
    },
    title: {
      text: 'Expenses Breakdown',
      style: { fontSize: '18px', fontWeight: 'bold' }
    },
    series: [{
      name: 'Expenses',
      data: [
        { name: 'Marketing', y: 30, color: '#EF4444' },
        { name: 'Operations', y: 40, color: '#F59E0B' },
        { name: 'Technology', y: 20, color: '#10B981' },
        { name: 'Other', y: 10, color: '#8B5CF6' }
      ]
    }],
    tooltip: {
      formatter: function() {
        return `<b>${this.point.name}</b><br/>${this.percentage.toFixed(1)}%`;
      }
    },
    credits: { enabled: false },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: 'pointer',
        dataLabels: {
          enabled: true,
          format: '<b>{point.name}</b>: {point.percentage:.1f}%'
        }
      }
    }
  };

  // Driver Earnings Chart
  const driverEarningsChartOptions = {
    chart: {
      type: 'column',
      backgroundColor: 'transparent',
      height: 400
    },
    title: {
      text: 'Driver Earnings',
      style: { fontSize: '18px', fontWeight: 'bold' }
    },
    xAxis: {
      categories: financialData.driverEarnings[selectedPeriod]?.map(item => 
        selectedPeriod === TIME_PERIOD.MONTHLY ? `${item.month} ${item.year}` :
        selectedPeriod === TIME_PERIOD.WEEKLY ? item.week :
        item.date
      ) || []
    },
    yAxis: {
      title: { text: 'Earnings ($)' },
      labels: {
        formatter: function() {
          return '$' + (this.value / 1000) + 'K';
        }
      }
    },
    series: [{
      name: 'Driver Earnings',
      data: financialData.driverEarnings[selectedPeriod]?.map(item => item.value) || [],
      color: '#10B981'
    }],
    tooltip: {
      formatter: function() {
        return `<b>${this.x}</b><br/>Earnings: ${formatCurrency(this.y)}`;
      }
    },
    credits: { enabled: false },
    legend: { enabled: false }
  };

  const reportColumns = [
    {
      key: 'name',
      header: 'Report Name',
      primary: true,
      render: (value, item) => (
        <div>
          <div className="font-medium text-gray-900">{value}</div>
          <div className="text-sm text-gray-500">{item.description}</div>
        </div>
      )
    },
    {
      key: 'type',
      header: 'Type',
      render: (value) => (
        <span className="capitalize font-medium text-gray-700">
          {value.replace('_', ' ')}
        </span>
      )
    },
    {
      key: 'period',
      header: 'Period',
      render: (value) => (
        <span className="capitalize text-gray-600">{value}</span>
      )
    },
    {
      key: 'lastGenerated',
      header: 'Last Generated',
      render: (value) => new Date(value).toLocaleDateString()
    },
    {
      key: 'isScheduled',
      header: 'Scheduled',
      render: (value) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          value ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
        }`}>
          {value ? 'Yes' : 'No'}
        </span>
      )
    }
  ];

  const reportActions = [
    {
      type: 'button',
      onClick: (item) => console.log('Generate report:', item),
      label: 'Generate',
      variant: 'primary'
    },
    {
      type: 'button',
      onClick: (item) => console.log('Download report:', item),
      label: 'Download',
      variant: 'secondary'
    }
  ];

  const kpiCards = [
    {
      title: 'Total Revenue',
      value: kpis?.totalRevenue?.current || 0,
      change: kpis?.totalRevenue?.change || 0,
      icon: IndianRupee,
      color: 'bg-green-50 border-green-200',
      iconColor: 'text-green-600'
    },
    {
      title: 'Net Profit',
      value: kpis?.netProfit?.current || 0,
      change: kpis?.netProfit?.change || 0,
      icon: TrendingUp,
      color: 'bg-blue-50 border-blue-200',
      iconColor: 'text-blue-600'
    },
    {
      title: 'Total Transactions',
      value: kpis?.totalTransactions?.current || 0,
      change: kpis?.totalTransactions?.change || 0,
      icon: Activity,
      color: 'bg-purple-50 border-purple-200',
      iconColor: 'text-purple-600',
      isCount: true
    },
    {
      title: 'Driver Earnings',
      value: kpis?.driverEarnings?.current || 0,
      change: kpis?.driverEarnings?.change || 0,
      icon: Users,
      color: 'bg-orange-50 border-orange-200',
      iconColor: 'text-orange-600'
    }
  ];

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'reports', label: 'Saved Reports', icon: FileText },
    { id: 'generate', label: 'Generate Report', icon: Plus }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Financial Reports</h1>
          <p className="mt-2 text-gray-600">
            Revenue analytics and financial insights
          </p>
        </div>
        <div className="flex items-center space-x-3 mt-4 sm:mt-0">
          <Select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            options={Object.values(TIME_PERIOD)}
            className="w-32"
          />
          <Button variant="primary">
            <Download size={16} className="mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon size={16} className="mr-2" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'dashboard' && (
        <div className="space-y-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {kpiCards.map((kpi, index) => (
              <div
                key={index}
                className={`${kpi.color} border rounded-xl p-6 transition-all duration-200 hover:shadow-md`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{kpi.title}</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">
                      {kpi.isCount 
                        ? kpi.value.toLocaleString()
                        : formatCurrency(kpi.value)
                      }
                    </p>
                    <div className="flex items-center mt-2">
                      <span className={`text-sm font-medium ${getChangeColor(kpi.change)}`}>
                        {formatPercentage(kpi.change)}
                      </span>
                      <span className="text-xs text-gray-500 ml-2">vs last period</span>
                    </div>
                  </div>
                  <div className={`${kpi.iconColor} p-3 rounded-lg bg-white/50`}>
                    <kpi.icon size={24} />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <HighchartsReact
                highcharts={Highcharts}
                options={revenueChartOptions}
              />
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <HighchartsReact
                highcharts={Highcharts}
                options={expensesChartOptions}
              />
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <HighchartsReact
              highcharts={Highcharts}
              options={driverEarningsChartOptions}
            />
          </div>
        </div>
      )}

      {activeTab === 'reports' && (
        <div className="space-y-6">
          <ResponsiveTable
            data={savedReports}
            columns={reportColumns}
            actions={reportActions}
          />
        </div>
      )}

      {activeTab === 'generate' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Generate New Report</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <Select
                label="Report Type"
                value={selectedReportType}
                onChange={(e) => setSelectedReportType(e.target.value)}
                options={Object.values(REPORT_TYPE)}
              />
              <Select
                label="Time Period"
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                options={Object.values(TIME_PERIOD)}
              />
            </div>
            <Button 
              onClick={handleGenerateReport} 
              variant="primary"
              disabled={isGenerating}
            >
              {isGenerating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Generating...
                </>
              ) : (
                <>
                  <FileText size={16} className="mr-2" />
                  Generate Report
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
